import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import {
  Alert,
  Autocomplete,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { usePagination } from '@tiles-erp/hooks';
import { ConfirmDialog, PageContainer } from '@tiles-erp/ui';
import type { PurchaseOrderItem, PurchaseOrderStatus } from '@tiles-erp/shared-types';
import { formatBoxPieces } from '@tiles-erp/shared';
import { DataTable } from '../../components/DataTable';
import { ApiError, apiFetch } from '../../lib/api-client';
import { useBranches } from '../products/branch-prices-api';
import { PurchaseOrderDialog } from './PurchaseOrderDialog';
import {
  usePurchaseOrder,
  usePurchaseOrders,
  usePurchaseOrderStatus,
  useSuppliers,
} from './api';
import { useSessionBranchId } from '../../lib/session-branch';
import { useBranding } from '../../app/branding';

const STATUS_COLORS: Record<PurchaseOrderStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> =
  {
    DRAFT: 'default',
    APPROVED: 'info',
    PARTIALLY_RECEIVED: 'warning',
    RECEIVED: 'success',
    CANCELLED: 'error',
  };

const STATUSES: PurchaseOrderStatus[] = [
  'DRAFT',
  'APPROVED',
  'PARTIALLY_RECEIVED',
  'RECEIVED',
  'CANCELLED',
];

const printPurchaseOrder = (
  order: PurchaseOrderItem,
  includePrice = true,
  popup = window.open('', '_blank'),
  branding: { appName: string; logo: string | null } = { appName: 'AVTHAR ERP', logo: null },
): void => {
  const rows = (order.lines ?? []).map((line, index) => {
    const qty = line.baseUom === 'PIECE'
      ? `${Math.round(line.qtyBoxes * line.piecesPerBox)} Pcs`
      : `${line.qtyBoxes} Box`;
    const sqft = line.baseUom === 'PIECE'
      ? (line.qtyBoxes * line.piecesPerBox * (line.sqftPerBox / Math.max(line.piecesPerBox, 1)))
      : line.qtyBoxes * line.sqftPerBox;
    return `<tr><td>${index + 1}</td><td>${line.productName}</td><td>${line.sizeMm ?? ''}</td><td class="n">${qty}</td><td class="n">${sqft > 0 ? `${sqft.toLocaleString('en-IN', { maximumFractionDigits: 2 })} SQFT` : ''}</td>${includePrice ? `<td class="n">${money(line.rate)}</td><td class="n">${line.gstRate}%</td><td class="n">${money(line.lineTotal)}</td>` : ''}</tr>`;
  }).join('');
  const totalBoxes = (order.lines ?? []).reduce((sum, line) => sum + line.qtyBoxes, 0);
  const totalSqft = (order.lines ?? []).reduce((sum, line) => sum + line.qtyBoxes * line.sqftPerBox, 0);
  const totalWeight = (order.lines ?? []).reduce((sum, line) => sum + line.qtyBoxes * (line.weightKg ?? 0), 0);
  const delivery = [order.deliveryAddressName, order.deliveryAddress, order.deliveryCity, order.deliveryState, order.deliveryPincode].filter(Boolean).join(', ');
  if (!popup) return;
  popup.document.write(`<!doctype html><html><head><title>${order.poNumber}</title><style>@page{size:A4;margin:9mm}*{box-sizing:border-box}body{font:11px Arial;color:#000;margin:0}.sheet{border:1.5px solid #000}.title{text-align:center;font:bold 21px Georgia;padding:7px;border-bottom:1px solid #000}.top{display:grid;grid-template-columns:52% 48%;border-bottom:1px solid #000}.company{min-height:125px;padding:9px;display:flex;gap:12px;align-items:flex-start}.logo{width:105px;max-height:90px;object-fit:contain}.company-name{font-weight:bold;font-size:14px;margin-bottom:5px}.po-info{display:grid;grid-template-columns:1fr 1fr}.cell{padding:7px;border-left:1px solid #000;border-bottom:1px solid #000;min-height:43px}.cell:nth-last-child(-n+2){border-bottom:0}.parties{display:grid;grid-template-columns:1fr 1fr}.party{padding:8px;min-height:105px;border-bottom:1px solid #000}.party+ .party{border-left:1px solid #000}.party-title{font-weight:bold;margin-bottom:4px}table{width:100%;border-collapse:collapse}th,td{border-right:1px solid #000;border-bottom:1px solid #000;padding:5px}th:last-child,td:last-child{border-right:0}th{text-align:left}.n{text-align:right}.summary td:first-child{text-align:right;font-weight:bold}.remarks{min-height:105px;padding:8px;line-height:1.65}.remarks-title{font-weight:bold;border-bottom:1px solid #000;margin:-8px -8px 7px;padding:5px 8px}.sign{display:flex;justify-content:space-between;padding:45px 12px 12px}</style></head><body><div class="sheet"><div class="title">Purchase Order</div><div class="top"><div class="company">${branding.logo ? `<img class="logo" src="${branding.logo}">` : ''}<div><div class="company-name">${order.branchName || branding.appName}</div>${order.branchAddress ? `<div>${order.branchAddress}</div>` : ''}${order.branchPhone ? `<div>Phone: ${order.branchPhone}</div>` : ''}${order.branchGstin ? `<div><b>GSTIN:</b> ${order.branchGstin}</div>` : ''}</div></div><div class="po-info"><div class="cell"><b>Purchase Order No.</b><br>${order.poNumber}</div><div class="cell"><b>Date</b><br>${new Date(order.orderDate).toLocaleDateString('en-IN')}</div><div class="cell"><b>Expected Date</b><br>${order.expectedDate ? new Date(order.expectedDate).toLocaleDateString('en-IN') : ''}</div><div class="cell"><b>Status</b><br>${order.status.replace('_', ' ')}</div></div></div><div class="parties"><div class="party"><div class="party-title">Seller</div><b>${order.supplierName}</b><br>${order.supplierAddress ?? ''}${order.supplierGstin ? `<br><b>GSTIN:</b> ${order.supplierGstin}` : ''}${order.supplierPhone ? `<br>Phone: ${order.supplierPhone}` : ''}</div><div class="party"><div class="party-title">Deliver To</div><b>${order.deliveryAddressName ?? order.branchName}</b><br>${delivery}</div></div><table><thead><tr><th style="width:35px">SR NO.</th><th>ITEM NAME</th><th style="width:90px">SIZE</th><th style="width:90px">QTY</th><th style="width:100px">SQFT</th>${includePrice ? '<th style="width:90px">RATE</th><th style="width:55px">GST</th><th style="width:100px">TOTAL</th>' : ''}</tr></thead><tbody>${rows}</tbody><tfoot><tr><th colspan="3" class="n">Total</th><th class="n">${totalBoxes.toLocaleString('en-IN', { maximumFractionDigits: 3 })}</th><th class="n">${totalSqft.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</th>${includePrice ? `<th></th><th></th><th class="n">${money(order.grandTotal)}</th>` : ''}</tr></tfoot></table><table class="summary"><tr><td>Total Weight:</td><td class="n">${totalWeight.toLocaleString('en-IN', { maximumFractionDigits: 2 })} KG</td></tr><tr><td>Total Packing Quantity:</td><td class="n">${totalBoxes.toLocaleString('en-IN', { maximumFractionDigits: 3 })} Box</td></tr>${includePrice ? `<tr><td>Sub Total:</td><td class="n">${money(order.subTotal)}</td></tr><tr><td>GST:</td><td class="n">${money(order.gstAmount)}</td></tr><tr><td>Grand Total:</td><td class="n"><b>${money(order.grandTotal)}</b></td></tr>` : ''}</table><div class="remarks"><div class="remarks-title">Overall Remarks / Delivery Terms</div>${order.remarks ? order.remarks.replace(/\n/g, '<br>') : ''}</div><div class="sign"><span>Supplier signature</span><span>Authorised signature</span></div></div><script>window.onload=()=>window.print()<\/script></body></html>`);
  popup.document.close();
};

const loadAndPrintPurchaseOrder = async (id: string, includePrice: boolean, branding: { appName: string; logo: string | null }): Promise<void> => {
  const popup = window.open('', '_blank');
  if (!popup) return;
  popup.document.write('<p style="font:14px Arial;padding:20px">Loading purchase order…</p>');
  try {
    const order = await apiFetch<PurchaseOrderItem>(`/purchase-orders/${id}`);
    popup.document.open();
    printPurchaseOrder(order, includePrice, popup, branding);
  } catch {
    popup.document.body.innerHTML = '<p style="font:14px Arial;padding:20px;color:#b00020">Unable to load the purchase order.</p>';
  }
};

const money = (value: number): string =>
  `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/** Purchase orders: draft, approve, cancel and inspect. */
export function PurchaseOrdersPage(): JSX.Element {
  const branding = useBranding();
  const pagination = usePagination();
  const suppliers = useSuppliers();
  const branches = useBranches();
  const [supplierId, setSupplierId] = useState('');
  const [branchId, setBranchId] = useSessionBranchId();
  const [status, setStatus] = useState<PurchaseOrderStatus | ''>('');

  const { data, isFetching } = usePurchaseOrders(pagination.query, {
    supplierId: supplierId || undefined,
    branchId: branchId || undefined,
    status: status || undefined,
  });

  const approve = usePurchaseOrderStatus('approve');
  const cancel = usePurchaseOrderStatus('cancel');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PurchaseOrderItem | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<PurchaseOrderItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const detail = usePurchaseOrder(viewingId);
  const editDetail = usePurchaseOrder(editing?.id ?? null);

  const runApprove = async (order: PurchaseOrderItem): Promise<void> => {
    setError(null);
    try {
      await approve.mutateAsync({ id: order.id, version: order.version });
      setNotice(`${order.poNumber} approved.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to approve');
    }
  };

  const columns = useMemo<ColDef<PurchaseOrderItem>[]>(
    () => [
      { field: 'poNumber', headerName: 'PO number', minWidth: 150 },
      {
        field: 'orderDate',
        headerName: 'Date',
        minWidth: 130,
        valueFormatter: (p) => (p.value ? new Date(p.value as string).toLocaleDateString() : ''),
      },
      { field: 'supplierName', headerName: 'Supplier', minWidth: 190 },
      { field: 'branchName', headerName: 'Branch', minWidth: 140 },
      { field: 'lineCount', headerName: 'Lines', maxWidth: 90 },
      {
        field: 'grandTotal',
        headerName: 'Total',
        maxWidth: 150,
        cellStyle: { fontWeight: 600 },
        valueFormatter: (p) => money(p.value as number),
      },
      {
        field: 'status',
        headerName: 'Status',
        maxWidth: 170,
        cellRenderer: (p: ICellRendererParams<PurchaseOrderItem>) => (
          <Chip
            label={(p.value as string).replace('_', ' ')}
            size="small"
            color={STATUS_COLORS[p.value as PurchaseOrderStatus]}
          />
        ),
      },
      {
        headerName: '',
        maxWidth: 230,
        cellRenderer: (p: ICellRendererParams<PurchaseOrderItem>) => {
          const order = p.data;
          if (!order) return null;
          const isDraft = order.status === 'DRAFT';
          const canCancel = order.status === 'DRAFT' || order.status === 'APPROVED';
          return (
            <>
              <Tooltip title="View lines">
                <IconButton size="small" onClick={() => setViewingId(order.id)}>
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print A4 with price">
                <IconButton size="small" onClick={() => void loadAndPrintPurchaseOrder(order.id, true, branding)}>
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print A4 without price">
                <IconButton size="small" onClick={() => void loadAndPrintPurchaseOrder(order.id, false, branding)}>
                  <PrintOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={isDraft ? 'Edit draft' : 'Only drafts can be edited'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!isDraft}
                    onClick={() => {
                      setEditing(order);
                      setDialogOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isDraft ? 'Approve' : 'Already approved'}>
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    disabled={!isDraft}
                    onClick={() => void runApprove(order)}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={canCancel ? 'Cancel' : 'Cannot cancel'}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={!canCancel}
                    onClick={() => setCancelling(order)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          );
        },
      },
    ],
    [branding],
  );

  return (
    <PageContainer
      title="Purchase orders"
      subtitle="Raise orders to suppliers; approved orders are ready for goods receipt."
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          New order
        </Button>
      }
    >
      <Stack spacing={1}>
        {notice && (
          <Alert severity="success" onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Autocomplete
            size="small"
            options={suppliers.data ?? []}
            getOptionLabel={(option) => `${option.name}${option.phone ? ` · ${option.phone}` : ''}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={(suppliers.data ?? []).find((supplier) => supplier.id === supplierId) ?? null}
            onChange={(_, supplier) => {
              setSupplierId(supplier?.id ?? '');
              pagination.setPage(1);
            }}
            sx={{ width: { xs: '100%', sm: 280 } }}
            renderInput={(params) => <TextField {...params} label="Supplier" placeholder="All suppliers" />}
          />
          <TextField
            select
            label="Branch"
            size="small"
            fullWidth={false}
            value={branchId}
            onChange={(e) => {
              setBranchId(e.target.value);
              pagination.setPage(1);
            }}
            sx={{ width: 180 }}
          >
            <MenuItem value="">All branches</MenuItem>
            {(branches.data ?? []).map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            size="small"
            fullWidth={false}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as PurchaseOrderStatus | '');
              pagination.setPage(1);
            }}
            sx={{ width: 180 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s.replace('_', ' ')}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <DataTable
          rows={data?.items ?? []}
          columns={columns}
          meta={data?.meta}
          pagination={pagination}
          loading={isFetching}
          searchPlaceholder="Search by PO number or supplier…"
          height={600}
        />
      </Stack>

      <PurchaseOrderDialog
        open={dialogOpen}
        editing={editing ? (editDetail.data ?? editing) : null}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSaved={(poNumber) => {
          setDialogOpen(false);
          setEditing(null);
          setNotice(`${poNumber} saved.`);
        }}
      />

      <Dialog open={viewingId !== null} onClose={() => setViewingId(null)} maxWidth="md" fullWidth>
        <DialogTitle>{detail.data?.poNumber ?? 'Purchase order'}</DialogTitle>
        <DialogContent>
          {detail.data && (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                {detail.data.supplierName} · {detail.data.branchName} ·{' '}
                {new Date(detail.data.orderDate).toLocaleDateString()}
                {detail.data.expectedDate
                  ? ` · expected ${new Date(detail.data.expectedDate).toLocaleDateString()}`
                  : ''}
              </Typography>
              {detail.data.remarks && (
                <Typography variant="body2">{detail.data.remarks}</Typography>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>SKU</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Boxes</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Disc %</TableCell>
                    <TableCell align="right">GST %</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Pending</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detail.data.lines ?? []).map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>{line.sku}</TableCell>
                      <TableCell>{line.productName}</TableCell>
                      <TableCell align="right">
                        {formatBoxPieces(line.qtyBoxes, line.piecesPerBox, line.baseUom === 'PIECE')}
                      </TableCell>
                      <TableCell align="right">{money(line.rate)}</TableCell>
                      <TableCell align="right">{line.discountPct}</TableCell>
                      <TableCell align="right">{line.gstRate}</TableCell>
                      <TableCell align="right">{money(line.lineTotal)}</TableCell>
                      <TableCell align="right">
                        {formatBoxPieces(
                          line.pendingBoxes,
                          line.piecesPerBox,
                          line.baseUom === 'PIECE',
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Stack direction="row" spacing={3} justifyContent="flex-end">
                <Typography variant="body2">Sub total: {money(detail.data.subTotal)}</Typography>
                <Typography variant="body2">GST: {money(detail.data.gstAmount)}</Typography>
                <Typography variant="subtitle2">
                  Total: {money(detail.data.grandTotal)}
                </Typography>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingId(null)}>Close</Button>
          <Button startIcon={<PrintOutlinedIcon />} disabled={!detail.data} onClick={() => detail.data && printPurchaseOrder(detail.data, false, undefined, branding)}>Print without price</Button>
          <Button variant="contained" startIcon={<PrintIcon />} disabled={!detail.data} onClick={() => detail.data && printPurchaseOrder(detail.data, true, undefined, branding)}>Print A4</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={cancelling !== null}
        title="Cancel purchase order"
        message={`Cancel ${cancelling?.poNumber}? This cannot be undone.`}
        destructive
        confirmLabel="Cancel order"
        cancelLabel="Keep"
        onCancel={() => setCancelling(null)}
        onConfirm={async () => {
          if (!cancelling) return;
          try {
            await cancel.mutateAsync({ id: cancelling.id, version: cancelling.version });
            setNotice(`${cancelling.poNumber} cancelled.`);
          } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Failed to cancel');
          } finally {
            setCancelling(null);
          }
        }}
      />
    </PageContainer>
  );
}
